/**
 * Outils de liste partagés (Batch #14.348) :
 *  - plages de dates : par défaut le MOIS EN COURS partout, raccourcis Ce mois · Mois précédent · Trimestre · Exercice · Tout ;
 *  - tri : en-têtes cliquables avec flèche ↑ / ↓ (croissant / décroissant), `useSort` + `sortItems`.
 */
import { useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const iso = (y, m0, d) => { const dt = new Date(y, m0, d); return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`; };

export const todayIso = () => { const d = new Date(); return iso(d.getFullYear(), d.getMonth(), d.getDate()); };
export const monthRange = (d = new Date()) => ({ from: iso(d.getFullYear(), d.getMonth(), 1), to: iso(d.getFullYear(), d.getMonth() + 1, 0) });
export const prevMonthRange = (d = new Date()) => ({ from: iso(d.getFullYear(), d.getMonth() - 1, 1), to: iso(d.getFullYear(), d.getMonth(), 0) });
export const quarterRange = (d = new Date()) => { const q = Math.floor(d.getMonth() / 3) * 3; return { from: iso(d.getFullYear(), q, 1), to: iso(d.getFullYear(), q + 3, 0) }; };
export const yearRange = (fyStartMonth = 1, d = new Date()) => {
  const m0 = Math.max(1, Math.min(12, Number(fyStartMonth) || 1)) - 1;
  const startYear = d.getMonth() >= m0 ? d.getFullYear() : d.getFullYear() - 1;
  return { from: iso(startYear, m0, 1), to: iso(startYear + 1, m0, 0) };
};

export const RANGE_PRESETS = [["month", "Ce mois"], ["prev_month", "Mois précédent"], ["quarter", "Trimestre"], ["year", "Exercice"], ["all", "Tout"]];
export function presetRange(id, fyStartMonth = 1) {
  if (id === "month") return monthRange();
  if (id === "prev_month") return prevMonthRange();
  if (id === "quarter") return quarterRange();
  if (id === "year") return yearRange(fyStartMonth);
  return { from: "", to: "" };
}
export function activePreset(from, to, fyStartMonth = 1) {
  return (RANGE_PRESETS.find(([id]) => { const r = presetRange(id, fyStartMonth); return r.from === (from || "") && r.to === (to || ""); }) || [null])[0];
}

const dateCls = "border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white";

/** Deux dates + raccourcis. `onChange({from,to})`. */
export function DateRangeBar({ from, to, onChange, fyStartMonth = 1, testId = "range", presets = true, className = "" }) {
  const active = activePreset(from, to, fyStartMonth);
  return (
    <div className={`flex items-center gap-1.5 flex-wrap ${className}`} data-testid={testId}>
      <input type="date" value={from || ""} onChange={(e) => onChange({ from: e.target.value, to })} className={dateCls} data-testid={`${testId}-from`} />
      <span className="text-xs text-slate-400">→</span>
      <input type="date" value={to || ""} onChange={(e) => onChange({ from, to: e.target.value })} className={dateCls} data-testid={`${testId}-to`} />
      {presets && (
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 ml-1" role="group" aria-label="Période rapide">
          {RANGE_PRESETS.map(([id, label]) => (
            <button key={id} type="button" onClick={() => onChange(presetRange(id, fyStartMonth))}
              className={`px-2 py-1 text-[11px] rounded-md transition-colors ${active === id ? "bg-slate-800 text-white font-semibold" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid={`${testId}-preset-${id}`} aria-pressed={active === id}>{label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────────── Tri ───────────── */
export function cmp(a, b) {
  const na = a === null || a === undefined || a === "", nb = b === null || b === undefined || b === "";
  if (na && nb) return 0;
  if (na) return 1;
  if (nb) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  const fa = typeof a === "string" && /^-?\d+([.,]\d+)?$/.test(a) ? parseFloat(a.replace(",", ".")) : null;
  const fb = typeof b === "string" && /^-?\d+([.,]\d+)?$/.test(b) ? parseFloat(b.replace(",", ".")) : null;
  if (fa !== null && fb !== null) return fa - fb;
  return String(a).localeCompare(String(b), "fr", { numeric: true, sensitivity: "base" });
}

/** Trie une copie. `getters[key]` optionnel : fonction (item) → valeur ; sinon item[key]. */
export function sortItems(items, key, dir = "asc", getters = {}) {
  if (!key) return items;
  const get = getters[key] || ((it) => it?.[key]);
  const out = [...(items || [])];
  out.sort((x, y) => cmp(get(x), get(y)) || 0);
  return dir === "desc" ? out.reverse() : out;
}

export function useSort(defaultKey = "date", defaultDir = "asc") {
  const [sort, setSort] = useState({ key: defaultKey, dir: defaultDir });
  const onSort = (key) => setSort((p) => (p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  return { sort, onSort, apply: (items, getters) => sortItems(items, sort.key, sort.dir, getters) };
}

/** En-tête de colonne triable : clic = bascule croissant ↔ décroissant. */
export function SortTh({ label, k, sort, onSort, className = "", align = "left", testId, title }) {
  const active = sort?.key === k;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`${className} select-none`} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => onSort(k)} title={title || (active ? (sort.dir === "asc" ? "Croissant — cliquer pour décroissant" : "Décroissant — cliquer pour croissant") : "Trier")}
        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-slate-800 ${align === "right" ? "flex-row-reverse" : ""} ${active ? "text-slate-800" : ""}`}
        data-testid={testId || `sort-${k}`}>
        <span>{label}</span>
        <Icon className={`w-3 h-3 ${active ? "text-emerald-600" : "text-slate-300"}`} />
      </button>
    </th>
  );
}
